import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import {
  Brain, BarChart2, ShieldCheck, Cpu, Map, Thermometer, Zap, Bot,
  ArrowRight, ExternalLink, ChevronRight, Activity, Layers, Users,
  BadgeCheck, FlaskConical, BarChart, Building2
} from "lucide-react";
import { WeeklyInicioWidget } from "../components/inicio/WeeklyInicioWidget";
import { TrendingChallenges } from "../components/inicio/TrendingChallenges";
import { GrantsRadarInicioWidget } from "../components/inicio/GrantsRadarInicioWidget";
import { BitacoraReminderWidget } from "../components/inicio/BitacoraReminderWidget";
import { PG07ReminderWidget } from "../components/inicio/PG07ReminderWidget";
import { FocusDayWidget } from "../components/inicio/FocusDayWidget";
import { VigilanciaInicioWidget } from "../components/inicio/VigilanciaInicioWidget";
import { OrphanCatalogWidget } from "../components/inicio/OrphanCatalogWidget";
import { ImpactoInicioWidget } from "../components/inicio/ImpactoInicioWidget";
import InnovationCycleFunnel from "../components/impacto/InnovationCycleFunnel";

// ── PROJECT DATA ──────────────────────────────────────────────────────────────
const PROJECTS = [
  {
    id: "pando-ai",
    name: "Pando AI",
    tagline: "Motor de Inteligencia Artificial Preventiva",
    description: "Analiza datos de plataformas de seguridad para generar insights accionables y recomendaciones preventivas basadas en evidencia. La solución digital con mayor madurez en Valoriza.",
    accent: "#8b5cf6",
    glow: "rgba(139,92,246,0.25)",
    borderColor: "rgba(139,92,246,0.4)",
    icon: Brain,
    metrics: [{ v: "IA", l: "Motor propio" }, { v: "360°", l: "Cobertura" }, { v: "24/7", l: "Análisis continuo" }],
    tags: ["IA Predictiva", "Big Data", "Seguridad"],
    status: "Activo",
    statusColor: "#22c55e",
  },
  {
    id: "hr360",
    name: "HR360",
    tagline: "People Analytics Corporativa",
    description: "Concentra datos históricos de personal, bajas, recaídas y absentismo. Segmenta por grupos, centros y demografía para convertir el dato en decisión preventiva.",
    accent: "#0ea5e9",
    glow: "rgba(14,165,233,0.25)",
    borderColor: "rgba(14,165,233,0.4)",
    icon: BarChart2,
    metrics: [{ v: "11,12%", l: "Absentismo total" }, { v: "Multi-seg.", l: "Centro · Sexo · Edad" }, { v: "Toda la empresa", l: "Alcance" }],
    tags: ["RRHH", "Analítica", "Prevención"],
    status: "Activo",
    statusColor: "#22c55e",
  },
  {
    id: "safety-ai",
    name: "Safety AI",
    tagline: "Dashboard de Seguridad en Tiempo Real",
    description: "Plataforma centralizada que consolida observaciones OPS, actividades de liderazgo y cuasi-accidentes con sincronización automática desde ArcGIS.",
    accent: "#10b981",
    glow: "rgba(16,185,129,0.25)",
    borderColor: "rgba(16,185,129,0.4)",
    icon: ShieldCheck,
    metrics: [{ v: "12.620", l: "Obs. OPS" }, { v: "20.729", l: "Asistentes" }, { v: "3,7%", l: "Incumplimiento RSV" }],
    tags: ["Tiempo Real", "ArcGIS", "SSO Entra ID"],
    status: "Activo",
    statusColor: "#22c55e",
  },
  {
    id: "halo-i",
    name: "Casco HALO‑I",
    tagline: "PPE Inteligente con Geovalla",
    description: "Piloto con geovalla para mantenimiento de márgenes de ADIF en Galicia. Casco con 8+ sensores integrados: GPS, temperatura, calidad del aire, SOS y detección de caídas.",
    accent: "#f59e0b",
    glow: "rgba(245,158,11,0.25)",
    borderColor: "rgba(245,158,11,0.4)",
    icon: Cpu,
    metrics: [{ v: "2", l: "Pilotos en curso" }, { v: "8+", l: "Sensores integrados" }, { v: "GPS", l: "Geolocalización" }],
    tags: ["IoT", "ADIF", "Smart PPE"],
    status: "Piloto",
    statusColor: "#f59e0b",
  },
  {
    id: "gemelo-digital",
    name: "Gemelo Digital",
    tagline: "Madrid en 3D con Capas de Calor",
    description: "Modelo tridimensional de Madrid con capas de temperatura e islas de calor. Herramienta clave para planificar actuaciones ante olas de calor extremo.",
    accent: "#3b82f6",
    glow: "rgba(59,130,246,0.25)",
    borderColor: "rgba(59,130,246,0.4)",
    icon: Map,
    metrics: [{ v: "Madrid", l: "Cobertura" }, { v: "3D", l: "Modelo urbano" }, { v: "Temperatura", l: "Capas térmicas" }],
    tags: ["GIS", "Digital Twin", "Clima"],
    status: "Activo",
    statusColor: "#22c55e",
  },
  {
    id: "canaria",
    name: "Canaria+",
    tagline: "Prevención de Golpes de Calor",
    description: "Wearable de temperatura corporal profunda con patente propia de Valoriza. Monitorización en tiempo real y alertas automáticas para prevenir el golpe de calor.",
    accent: "#ef4444",
    glow: "rgba(239,68,68,0.25)",
    borderColor: "rgba(239,68,68,0.4)",
    icon: Thermometer,
    metrics: [{ v: "172", l: "Trabajadores" }, { v: "88,2%", l: "Recuperación <30 min" }, { v: "85", l: "Alarmas gestionadas" }],
    tags: ["Wearable", "Patente propia", "Salud"],
    status: "Piloto",
    statusColor: "#f59e0b",
  },
  {
    id: "exoesqueleto",
    name: "Exoesqueleto",
    tagline: "Hipershell — Asistencia Ergonómica",
    description: "Exoesqueleto testado en entorno real durante la recogida de muebles y enseres en San Fernando. Reduce la carga lumbar y da asistencia activa en hombros.",
    accent: "#a855f7",
    glow: "rgba(168,85,247,0.25)",
    borderColor: "rgba(168,85,247,0.4)",
    icon: Zap,
    metrics: [{ v: "San Fernando", l: "Piloto real" }, { v: "Lumbar", l: "Carga reducida" }, { v: "Hombros", l: "Asistencia activa" }],
    tags: ["Ergonomía", "Hipershell", "Residuos"],
    status: "Piloto",
    statusColor: "#f59e0b",
  },
  {
    id: "robotica",
    name: "Robótica",
    tagline: "Humanoide y Cuadrúpedo en Operaciones",
    description: "Despliegue de robots humanoide y cuadrúpedo en operaciones reales de limpieza e inspección de infraestructuras urbanas.",
    accent: "#64748b",
    glow: "rgba(100,116,139,0.25)",
    borderColor: "rgba(100,116,139,0.4)",
    icon: Bot,
    metrics: [{ v: "2 tipos", l: "Humanoide + Cuadrúpedo" }, { v: "Real", l: "Operaciones activas" }, { v: "Inspección", l: "Limpieza urbana" }],
    tags: ["Robótica", "Autonomía", "Urbano"],
    status: "Piloto",
    statusColor: "#f59e0b",
  },
];

// Valores de respaldo mientras cargan los datos reales (evita parpadeo a 0)
const STATS_FALLBACK = [
  { value: 97, suffix: "", label: "Proyectos en curso", icon: FlaskConical, color: "#10b981" },
  { value: 64, suffix: "", label: "Pilotos en curso", caption: "pendientes + extendidos", icon: Activity, color: "#f59e0b" },
  { value: 172, suffix: "+", label: "Trabajadores protegidos (Canaria+)", icon: Users, color: "#3b82f6" },
  { value: 19, suffix: "", label: "Patentes propias", icon: BadgeCheck, color: "#8b5cf6" },
];

// ── ANIMATED COUNTER ──────────────────────────────────────────────────────────
// Bug real 2026-08-31 (confirmado por el usuario en Live, recurrente desde hace
// semanas): el contador acumulaba `cur += target/steps` 40 veces y hacía
// `Math.floor(cur)` — la suma en coma flotante casi nunca llega EXACTA al target
// (ej. target=82 → cur final = 81.99999999999994 → floor = 81), así que Inicio
// mostraba sistemáticamente 1 menos que el valor real cada vez que el objetivo
// no era divisible de forma exacta por 40 pasos, mientras el Panel (sin
// animación, pinta el número crudo) siempre mostraba el valor correcto. Fix:
// cada paso se calcula desde `i/steps` (sin acumular error) y el ÚLTIMO paso
// fija el valor EXACTO a `target`, nunca una aproximación. Además, una vez
// completada la animación una vez, los refrescos en segundo plano (foco/60s)
// ya no re-animan desde 0 — solo actualizan el número directamente, evitando
// que alguien pueda "pillar" un número a medio camino en un refresco posterior.
function Counter({ target, suffix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const hasAnimatedRef = useRef(false);
  useEffect(() => {
    if (!inView) return;
    if (hasAnimatedRef.current) {
      setVal(target);
      return;
    }
    hasAnimatedRef.current = true;
    const dur = 1600, steps = 40;
    let i = 0;
    const t = setInterval(() => {
      i++;
      if (i >= steps) {
        setVal(target);
        clearInterval(t);
      } else {
        setVal(Math.round((i / steps) * target));
      }
    }, dur / steps);
    return () => clearInterval(t);
  }, [inView, target]);
  return <span ref={ref}>{val.toLocaleString("es-ES")}{suffix}</span>;
}

// ── ANIMATED GRID BG ─────────────────────────────────────────────────────────
function GridBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* Grid lines */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
        backgroundSize: "64px 64px",
      }} />
      {/* Glow orbs */}
      <div style={{ position: "absolute", top: "-20%", left: "10%", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,121,52,0.12) 0%, transparent 70%)", filter: "blur(60px)" }} />
      <div style={{ position: "absolute", top: "40%", right: "5%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(139,92,246,0.10) 0%, transparent 70%)", filter: "blur(60px)" }} />
      <div style={{ position: "absolute", bottom: "10%", left: "30%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(14,165,233,0.08) 0%, transparent 70%)", filter: "blur(60px)" }} />
    </div>
  );
}

// ── PROJECT CARD ─────────────────────────────────────────────────────────────
function ProjectCard({ project, index }) {
  const Icon = project.icon;
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 + index * 0.07, ease: "easeOut" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered
          ? `linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)`
          : `linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)`,
        border: `1px solid ${hovered ? project.accent + "70" : project.borderColor}`,
        borderRadius: 16,
        padding: "24px",
        cursor: "default",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        boxShadow: hovered ? `0 0 32px ${project.glow}, 0 8px 40px rgba(0,0,0,0.3)` : "0 2px 16px rgba(0,0,0,0.2)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
      }}
    >
      {/* Accent top bar */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${project.accent}, transparent)`, borderRadius: "16px 16px 0 0" }} />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: `${project.accent}20`, border: `1px solid ${project.accent}40`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon size={20} color={project.accent} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#f1f5f9", fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}>{project.name}</div>
            <div style={{ fontSize: 11, color: project.accent, fontWeight: 500, marginTop: 2, opacity: 0.9 }}>{project.tagline}</div>
          </div>
        </div>
        <span style={{
          fontSize: 10, fontWeight: 600, padding: "3px 10px", borderRadius: 20,
          background: `${project.statusColor}18`, border: `1px solid ${project.statusColor}40`,
          color: project.statusColor, letterSpacing: "0.05em", whiteSpace: "nowrap"
        }}>{project.status}</span>
      </div>

      {/* Description */}
      <p style={{ fontSize: 13, color: "rgba(241,245,249,0.55)", lineHeight: 1.6, marginBottom: 18, minHeight: 56 }}>
        {project.description}
      </p>

      {/* Metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 16 }}>
        {project.metrics.map((m, i) => (
          <div key={i} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "8px 6px", textAlign: "center", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#f1f5f9", fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}>{m.v}</div>
            <div style={{ fontSize: 10, color: "rgba(241,245,249,0.4)", marginTop: 2, lineHeight: 1.3 }}>{m.l}</div>
          </div>
        ))}
      </div>

      {/* Tags */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {project.tags.map((tag, i) => (
          <span key={i} style={{
            fontSize: 10, padding: "2px 8px", borderRadius: 20,
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
            color: "rgba(241,245,249,0.5)", fontWeight: 500
          }}>{tag}</span>
        ))}
      </div>
    </motion.div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
export default function InicioPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const firstName = user?.full_name?.split(" ")[0] || "Innovador";
  const [stats, setStats] = useState(STATS_FALLBACK);
  // Bug real 2026-08-24 (verificado en Producción): el Counter animado arranca en
  // cuanto entra en viewport, usando el valor de RESPALDO como target (97/64/172/19)
  // porque la llamada a /dashboard/stats aún no ha resuelto. Cuando los datos reales
  // llegan, `target` cambia y el efecto del Counter se reinicia desde 0 hacia el
  // nuevo target — así que quien mira la pantalla en ese instante (recién cargada)
  // puede ver un número a medio camino ("76" en vez de "77", "0"/"17" en vez de "18")
  // sin que haya NINGÚN dato incorrecto de por medio (el backend siempre devolvió
  // los valores correctos). Fix: no animar hasta tener el dato real; con el
  // respaldo se muestra el número fijo (sin Counter) y solo se anima UNA vez,
  // con el valor final correcto, cuando `statsLoaded` pasa a true.
  const [statsLoaded, setStatsLoaded] = useState(false);

  // Contadores en vivo (UX Fase 2, 2026-08-21): antes eran numeros fijos en el
  // codigo ("8 proyectos activos", "4+ pilotos", "2 patentes") que no coincidian
  // con Portfolio/Pilotos/Patentes reales. Ahora se leen de /dashboard/stats.
  useEffect(() => {
    if (!token) return;
    // Fix 2026-08-24 (2ª vuelta): antes Inicio calculaba estos 3 números en el
    // cliente filtrando /initiatives y /pilots por su cuenta — misma fórmula que
    // el Panel pero ejecutada por separado, así que un desfase de 1 doc entre la
    // carga de Inicio y la del Panel (alguien editando en producción, o timing)
    // podía producir "76 vs 77" / "81 vs 82" aunque la fórmula fuera idéntica.
    // Ahora Inicio reutiliza el MISMO endpoint que ya usa el Panel
    // (/dashboard/stats) para estos 3 números: por construcción nunca pueden
    // desincronizarse entre pantallas. Cada campo se aplica de forma independiente
    // (si `patents_count` faltara en la respuesta, esa tarjeta mantiene su
    // respaldo sin afectar a las otras 2 — así se evitó también el "Patentes
    // propias: 0" del bloque anterior).
    // Fix 2026-08-28: el reporte recurrente de "81/76 vs 82/77" venía de que esta
    // llamada solo se hacía UNA VEZ al montar — si alguien deja Inicio abierto en
    // una pestaña sin recargar y compara con un Panel recién abierto, ve datos
    // reales de momentos distintos (parece un bug, es una pestaña vieja). Ahora se
    // refresca también al recuperar el foco de la pestaña y cada 60s en segundo plano.
    const load = async () => {
      try {
        const dashStats = await api.getDashboardStats(token);
        setStats((prev) => [
          { ...prev[0], value: dashStats?.active_initiatives ?? prev[0].value },
          { ...prev[1], value: dashStats?.pilots_count ?? prev[1].value },
          prev[2],
          { ...prev[3], value: dashStats?.patents_count ?? prev[3].value },
        ]);
      } catch { /* mantiene el respaldo en las 4 tarjetas */ }
      finally { setStatsLoaded(true); }
    };
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const interval = setInterval(load, 60000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(interval); };
  }, [token]);

  return (
    <div style={{
      minHeight: "100vh", background: "#060b14", position: "relative",
      fontFamily: "'Outfit', 'Inter', sans-serif", overflowX: "hidden",
    }}>
      <GridBackground />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 1400, margin: "0 auto", padding: "40px 32px 80px" }}>

        {/* ── HERO ──────────────────────────────────────────────────────────── */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} style={{ marginBottom: 56 }}>
          {/* Top pill */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(0,121,52,0.15)", border: "1px solid rgba(0,121,52,0.4)", borderRadius: 20, padding: "6px 14px", marginBottom: 24 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#10b981", boxShadow: "0 0 8px #10b981", display: "inline-block" }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#34d399", letterSpacing: "0.08em" }}>SISTEMA OPERATIVO DE INNOVACIÓN</span>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: 24 }}>
            <div>
              <h1 style={{ fontSize: "clamp(32px, 5vw, 56px)", fontWeight: 800, color: "#f1f5f9", lineHeight: 1.1, marginBottom: 12, letterSpacing: "-0.02em" }}>
                Innovación en Acción
                <br />
                <span style={{ background: "linear-gradient(135deg, #007934, #83c61a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Valoriza 2026</span>
              </h1>
              <p style={{ fontSize: 16, color: "rgba(241,245,249,0.5)", maxWidth: 560, lineHeight: 1.6 }}>
                Bienvenido{user?.full_name ? `, ${firstName}` : ""}. Aquí tienes el estado real de todos los proyectos activos de innovación y prevención.
              </p>
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button
                data-testid="inicio-cta-proyectos"
                onClick={() => navigate("/projects")}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: "linear-gradient(135deg, #007934, #3BAD2D)",
                  color: "#fff", border: "none", borderRadius: 10,
                  padding: "11px 20px", fontWeight: 600, fontSize: 14, cursor: "pointer",
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={e => e.target.style.opacity = "0.85"}
                onMouseLeave={e => e.target.style.opacity = "1"}
              >
                Proyectos I+D <ArrowRight size={16} />
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: "rgba(255,255,255,0.06)", color: "#cbd5e1",
                  border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10,
                  padding: "11px 20px", fontWeight: 600, fontSize: 14, cursor: "pointer",
                  transition: "background 0.2s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.06)"}
              >
                <BarChart size={16} /> Dashboard Ejecutivo
              </button>
            </div>
          </div>
        </motion.div>

        {/* ── STATS ─────────────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 16, marginBottom: 56,
          }}
        >
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} style={{
                background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 14, padding: "22px 24px", display: "flex", alignItems: "center", gap: 14,
              }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `${s.color}18`, border: `1px solid ${s.color}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={20} color={s.color} />
                </div>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: "#f1f5f9", lineHeight: 1, fontFamily: "'Outfit', sans-serif" }}>
                    {statsLoaded
                      ? <Counter key={`loaded-${i}`} target={s.value} suffix={s.suffix} />
                      : <span>{s.value.toLocaleString("es-ES")}{s.suffix}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(241,245,249,0.45)", marginTop: 4, fontWeight: 500 }}>{s.label}</div>
                  {s.caption && (
                    <div style={{ fontSize: 10, color: "rgba(241,245,249,0.3)", marginTop: 1 }} data-testid={`inicio-stat-caption-${i}`}>({s.caption})</div>
                  )}
                </div>
              </div>
            );
          })}
        </motion.div>

        {/* ── IMPACTO PRODUCCIÓN (CO2/€ ahorro anual) ─────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <ImpactoInicioWidget />
        </div>

        {/* ── BITÁCORA REMINDER (días sin registrar esta semana) ──────────── */}
        <div style={{ marginBottom: 16 }}>
          <BitacoraReminderWidget />
        </div>

        {/* ── PG-07 REMINDER (fichas pendientes de tus proyectos) ─────────── */}
        <div style={{ marginBottom: 16 }}>
          <PG07ReminderWidget />
        </div>

        {/* ── ALERTA VINCULACIONES HUÉRFANAS (Catálogo Innovaciones) ──────── */}
        <div style={{ marginBottom: 16 }}>
          <OrphanCatalogWidget />
        </div>

        {/* ── CICLO INNOVACIÓN (Concept → Producción) ─────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <InnovationCycleFunnel variant="compact" />
        </div>

        {/* ── VIGILANCIA TECNOLÓGICA (señales críticas) ───────────────────── */}
        <VigilanciaInicioWidget />

        {/* ── FOCUS DAY (mis compromisos) ─────────────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <FocusDayWidget token={localStorage.getItem("token") || sessionStorage.getItem("token")} />
        </div>

        {/* ── WEEKLY WIDGET ─────────────────────────────────────────────────── */}
        <WeeklyInicioWidget />

        {/* ── GRANTS RADAR (deadlines más próximos) ────────────────────────── */}
        <GrantsRadarInicioWidget />

        {/* ── TRENDING CHALLENGES (NOVA) ───────────────────────────────────── */}
        <TrendingChallenges />

        {/* ── SECTION HEADER ────────────────────────────────────────────────── */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} style={{ marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: "#f1f5f9", marginBottom: 4 }}>Proyectos Activos</h2>
            <p style={{ fontSize: 13, color: "rgba(241,245,249,0.4)" }}>Tecnología real operando en nuestras ciudades — Innovation Day 2026</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <span style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)", color: "#4ade80", fontWeight: 600 }}>● Activo</span>
            <span style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.3)", color: "#fbbf24", fontWeight: 600 }}>● Piloto</span>
          </div>
        </motion.div>

        {/* ── PROJECT GRID ──────────────────────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 20, marginBottom: 56 }}>
          {PROJECTS.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
        </div>

        {/* ── PARTNERS STRIP ────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          style={{
            background: "rgba(255,255,255,0.025)",
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 16, padding: "28px 36px",
            display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 20,
          }}
        >
          <div>
            <p style={{ fontSize: 11, color: "rgba(241,245,249,0.35)", fontWeight: 600, letterSpacing: "0.1em", marginBottom: 8 }}>COLABORADORES & PARTNERS</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "center" }}>
              {["IRSST · Instituto Regional de Seguridad y Salud", "Comunidad de Madrid", "ADIF", "Hipershell"].map((p, i) => (
                <span key={i} style={{ fontSize: 13, fontWeight: 600, color: "rgba(241,245,249,0.55)", display: "flex", alignItems: "center", gap: 6 }}>
                  <Building2 size={13} color="rgba(241,245,249,0.3)" /> {p}
                </span>
              ))}
            </div>
          </div>
          <button
            data-testid="inicio-cta-partners-proyectos"
            onClick={() => navigate("/projects")}
            style={{
              display: "flex", alignItems: "center", gap: 8, background: "transparent",
              color: "#4ade80", border: "1px solid rgba(74,222,128,0.3)", borderRadius: 10,
              padding: "10px 20px", fontWeight: 600, fontSize: 13, cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(74,222,128,0.08)"; e.currentTarget.style.borderColor = "rgba(74,222,128,0.5)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(74,222,128,0.3)"; }}
          >
            Ver todos los proyectos I+D <ChevronRight size={15} />
          </button>
        </motion.div>

        {/* ── FOOTER NOTE ───────────────────────────────────────────────────── */}
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <p style={{ fontSize: 12, color: "rgba(241,245,249,0.2)", fontStyle: "italic" }}>
            Innovation OS · Grupo Valoriza · {new Date().getFullYear()} — Plataforma interna de gestión de innovación
          </p>
        </div>
      </div>
    </div>
  );
}
